import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Map, GraduationCap, ListChecks, RefreshCw, FileCheck } from "lucide-react";
import { METHOD } from "@/data/content";
import { Reveal, SectionLabel } from "./primitives";

const ICONS = [Map, GraduationCap, ListChecks, RefreshCw, FileCheck];

export default function MethodSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.7", "end 0.6"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="metodo" className="relative overflow-hidden bg-brand-navy py-24 text-white md:py-32">
      <div className="noise-overlay pointer-events-none absolute inset-0 opacity-[0.05]" />
      <div className="pointer-events-none absolute -right-20 top-10 h-96 w-96 rounded-full bg-brand-blue/25 blur-[130px]" />
      <div className="pointer-events-none absolute -left-20 bottom-10 h-80 w-80 rounded-full bg-brand-gold/10 blur-[120px]" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          {/* Sticky heading */}
          <div className="lg:sticky lg:top-32 lg:h-fit">
            <Reveal><SectionLabel dark>O MÉTODO</SectionLabel></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white md:text-5xl lg:text-6xl">
                Do edital <br />
                <span className="gold-gradient-text animate-shimmer">à prova.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
                {METHOD.subtitle}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-8 font-serif text-2xl italic text-white/75 md:text-3xl">
                “Conteúdo em estratégia.”
              </p>
            </Reveal>
          </div>

          {/* Steps with filling line */}
          <div ref={ref} className="relative pl-10 md:pl-14">
            <div className="absolute left-4 top-2 bottom-2 w-px bg-white/15 md:left-5" />
            <motion.div
              style={{ height: lineHeight }}
              className="absolute left-4 top-2 w-px bg-brand-gold md:left-5"
            />

            <div className="flex flex-col gap-6">
              {METHOD.steps.map((step, i) => {
                const Icon = ICONS[i];
                return (
                  <motion.div
                    key={step.n}
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    className="group relative"
                  >
                    <span className="absolute -left-[38px] top-6 grid h-8 w-8 place-items-center rounded-full bg-brand-navy ring-1 ring-white/15 md:-left-[46px]">
                      <span className="h-2.5 w-2.5 rounded-full bg-brand-gold" />
                    </span>
                    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-[transform,box-shadow,background-color,border-color] duration-300 hover:-translate-y-1 hover:border-brand-gold/30 hover:bg-white/[0.07] md:p-7">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-gold/15 text-brand-gold">
                            <Icon className="h-5 w-5" />
                          </span>
                          <div>
                            <span className="font-display text-xs font-semibold tracking-[0.2em] text-brand-gold">
                              {step.n}
                            </span>
                            <h3 className="font-display text-lg font-semibold tracking-tight text-white md:text-xl">
                              {step.title}
                            </h3>
                          </div>
                        </div>
                      </div>
                      <p className="mt-4 text-sm leading-relaxed text-white/60 md:text-base">
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
